import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Box, IconButton, Stack } from '@mui/material';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiResultModal } from '../../../components/modal/ApiResultHandler';
import { ApproveModal } from '../../../components/modal/ApproveModal';
import { useModalToggle } from '../../../components/modal/Modal';
import {
  PrimaryButton,
  WarningButton,
} from '../../../components/style/ButtonStyle';
import { CommonDivider } from '../../../components/style/CommonStyle';
import {
  CountingText,
  DetailInfoText,
  FormLabelText,
  FormTitle,
} from '../../../components/style/TextStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import useResponsive from '../../../hooks/useResponsive';
import {
  UserRoleListLabel,
  responsiveMargin,
  responsiveSpacing,
} from '../../../utils/enum';
import DeleteUserButton from './Common/DeleteUserButton';

export default function UserDetailPage() {
  const navigate = useNavigate();
  const mdUp = useResponsive('up', 'md');
  const { userId } = useParams();
  const [getUserResult] = useAuthAxios(
    {
      method: 'GET',
      url: `/api/v1/users/${userId}`,
    },
    { manual: false },
  );

  const user = _.get(getUserResult, 'response.data');

  return (
    <Stack mx={responsiveMargin} spacing={5} mt={{ xs: 2, md: 10 }} mb={5}>
      <Stack
        direction={{ xs: 'row', md: 'column' }}
        spacing={{ xs: 0, md: 5 }}
        justifyContent={{ xs: 'space-between' }}
        alignItems={{ xs: 'center', md: 'flex-start' }}
      >
        <IconButton
          sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
          onClick={() => navigate(`/users`)}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        {mdUp ? (
          <DesktopHeader userId={userId} />
        ) : (
          <>
            <FormTitle>이용자 상세정보</FormTitle>
            <Box sx={{ width: '40px' }} />
          </>
        )}
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={3}
      >
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>
            ID<span>*</span>
          </FormLabelText>
          <DetailInfoText>{user?.id || '-'}</DetailInfoText>
        </Stack>
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>
            이름<span>*</span>
          </FormLabelText>
          <DetailInfoText>{user?.name || '-'}</DetailInfoText>
        </Stack>
      </Stack>
      {mdUp ? <CommonDivider /> : <></>}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={3}
      >
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>
            이메일 주소<span>*</span>
          </FormLabelText>
          <DetailInfoText>{user?.email || '-'}</DetailInfoText>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent={{ xs: 'space-between', md: 'flex-start' }}
          >
            <CountingText>
              <span>*</span> 비밀번호를 잊어버리셨나요?
            </CountingText>
            <IssueTempPWButton userEmail={user?.email} />
          </Stack>
        </Stack>
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>
            연락처<span>*</span>
          </FormLabelText>
          <DetailInfoText>{user?.phone || '-'}</DetailInfoText>
        </Stack>
      </Stack>
      {mdUp ? <CommonDivider /> : <></>}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={3}
      >
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>행정동</FormLabelText>
          <DetailInfoText>{user?.administrativeRegion || '-'}</DetailInfoText>
        </Stack>
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>소속과</FormLabelText>
          <DetailInfoText>{user?.departmentTitle || '-'}</DetailInfoText>
        </Stack>
        <Stack spacing={responsiveSpacing} sx={{ flex: 1 }}>
          <FormLabelText>권한</FormLabelText>
          <DetailInfoText>
            {_.get(UserRoleListLabel, user?.role, '-')}
          </DetailInfoText>
        </Stack>
      </Stack>
      {mdUp ? (
        <></>
      ) : (
        <Stack direction="row" spacing={1}>
          <DeleteUserButton userId={userId} />
          <PrimaryButton onClick={() => navigate(`/users/form/${userId}`)}>
            계정 수정
          </PrimaryButton>
        </Stack>
      )}
    </Stack>
  );
}

function DesktopHeader({ userId }: any) {
  const navigate = useNavigate();
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100% !important' }}
      >
        <FormTitle>이용자 상세정보</FormTitle>
        <Box />
        <Stack direction="row" spacing={1}>
          <DeleteUserButton
            userId={userId}
            styleProps={{ width: 130, height: 40 }}
          />
          <PrimaryButton
            sx={{ width: 206, height: 40 }}
            onClick={() => navigate(`/users/form/${userId}`)}
          >
            계정 수정
          </PrimaryButton>
        </Stack>
      </Stack>
      <CommonDivider />
    </>
  );
}

function IssueTempPWButton({ userEmail }) {
  const [postTempPWResult, postTempPW] = useAuthAxios({
    method: 'POST',
    url: `/api/v1/user/send/password`,
  });
  const { on, open, close } = useModalToggle();

  return (
    <>
      <WarningButton onClick={open} sx={{ maxWidth: 158, fontSize: 12 }}>
        임시 비밀번호 전송
      </WarningButton>
      <ApproveModal
        on={on}
        onConfirm={() => postTempPW({ data: { email: userEmail } })}
        onClose={close}
        title="임시 비밀번호를 발송하시겠습니까?"
        children={
          <CountingText sx={{ textAlign: 'center', mb: 2 }}></CountingText>
        }
        isPrimaryColor={false}
      />
      <ApiResultModal
        apiResult={postTempPWResult}
        successCode={201}
        successTitle="임시 비밀번호 발송 완료"
        successContent={
          <>
            이용자 정보에 등록된 메일로 <br /> 임시 비밀번호가 발송되었습니다.
          </>
        }
        onSuccessClose={close}
        failTitle="임시 비밀번호 메일 발송 실패"
        onFailClose={close}
        ErrorCodeTable={{
          EMESSAGE: { title: '승인되지 않은 이메일입니다.' },
        }}
      ></ApiResultModal>
    </>
  );
}
